import { ApiClientProvider, bootstrapApiClient } from '@commander/api-client';
import {
  AuthProvider,
  getValidatedAuthToken,
  handleTokenExpired,
  handleUnauthorized,
  setAuthApiClient,
  useAuth,
} from '@commander/auth';
import { FeatureFlagProvider } from '@commander/feature-flags';
import {
  addHttpBreadcrumb,
  ErrorBoundary,
  initObservability,
  ObservabilityProvider,
} from '@commander/observability';
import { initAnalytics } from '@commander/product-analytics';
import { ThemeChangerProvider } from '@commander/theme-changer';
import { ThemeProvider, Toaster } from '@commander/ui';
import { UserDetailsProvider } from '@commander/user-details';
import { defaultQueryConfig, setupChunkLoadErrorHandler } from '@commander/utils';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { BASE_URL } from './utils/Constant';
import './index.css';
import App from './App.tsx';
import { relayMailboxConnectResult } from './features/action-dash/utils/mailboxConnect';
import { StripeProvider } from './providers/StripeProvider';

setupChunkLoadErrorHandler();

/**
 * Run a non-essential bootstrap step (telemetry/analytics) without letting a
 * failure abort module execution before React mounts. Some environments — notably
 * Safari Private Browsing — restrict storage/cookies in ways that can make Sentry
 * or Mixpanel init throw. Telemetry must NEVER blank the app (in particular the
 * public signer landing page, which external users hit unauthenticated).
 */
function safeBootstrap(label: string, fn: () => void) {
  try {
    fn();
  } catch (err) {
    console.warn(`[shell] non-essential bootstrap "${label}" failed; continuing`, err);
  }
}

// Initialize observability (Sentry + Web Vitals) before React renders, so early
// errors are captured — but never at the cost of the app failing to render.
safeBootstrap('observability', () =>
  initObservability({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    tracesSampleRate: 0.1, // 10% of transactions for performance monitoring
    profilesSampleRate: 0.3, // 30% of sampled transactions for profiling
    enableWebVitals: true,
    debug: import.meta.env.DEV, // Enable debug logging in development
  })
);

// Initialize product analytics (Mixpanel) for all micro-apps at the platform
// level (micro-apps are lazy-loaded).
safeBootstrap('analytics', () =>
  initAnalytics({
    VITE_MIXPANEL_TOKEN: import.meta.env.VITE_MIXPANEL_TOKEN,
    VITE_MIXPANEL_API_HOST: import.meta.env.VITE_MIXPANEL_API_HOST,
    VITE_ANALYTICS_ENABLED: import.meta.env.VITE_ANALYTICS_ENABLED,
    VITE_ANALYTICS_DEBUG: import.meta.env.VITE_ANALYTICS_DEBUG,
  })
);

// Initialize API infrastructure with bootstrap helper
// Wires together axios + ApiClient + auth token providers + observability
const apiClient = bootstrapApiClient({
  baseURL: BASE_URL,
  getToken: getValidatedAuthToken,
  onTokenExpired: handleTokenExpired,
  onUnauthorized: handleUnauthorized,
  onHttpBreadcrumb: addHttpBreadcrumb, // Track HTTP requests in Sentry
});

// Set ApiClient for auth package (used by Zustand stores and non-React code)
setAuthApiClient(apiClient);

// Create QueryClient with shared config from utils
const queryClient = new QueryClient(defaultQueryConfig);

/**
 * Inner app component that has access to auth context
 * This allows ObservabilityProvider to sync user context
 */
function AppWithObservability() {
  const { user, isAuthenticated } = useAuth();

  return (
    <ObservabilityProvider user={user} isAuthenticated={isAuthenticated}>
      <FeatureFlagProvider apiClient={apiClient}>
        <UserDetailsProvider apiClient={apiClient}>
          <ThemeChangerProvider>
            <ErrorBoundary featureName="Shell">
              <App />
            </ErrorBoundary>
            <Toaster />
          </ThemeChangerProvider>
        </UserDetailsProvider>
      </FeatureFlagProvider>
    </ObservabilityProvider>
  );
}

// A mailbox-connect popup from Action Dash only hands its result back and closes.
if (!relayMailboxConnectResult()) {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ApiClientProvider client={apiClient}>
          <BrowserRouter>
            <ThemeProvider defaultTheme="system">
              <AuthProvider>
                <StripeProvider>
                  <AppWithObservability />
                </StripeProvider>
              </AuthProvider>
            </ThemeProvider>
          </BrowserRouter>
        </ApiClientProvider>
      </QueryClientProvider>
    </StrictMode>
  );
}
